import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  IconButton,
  Avatar,
  useTheme
} from '@mui/material';
import BalanceIcon from '@mui/icons-material/Balance';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { DebitoIcmsEstruturaMercadologicaSecaoGrafico, Secao, SecaoICMS } from './types';
import { HierarchicalTableSkeleton } from '../../Components/Skeletons/ChartSkeletons';

// interface Subgrupo {
//   descricao: string;
//   credito_icms: number;
//   debito_icms: number;
//   saldo_icms: number;
// }
//
// interface Grupo {
//   descricao: string;
//   credito_icms: number;
//   debito_icms: number;
//   saldo_icms: number;
//   subgrupos: Subgrupo[];
// }

// interface Secao {
//   descricao: string;
//   credito_icms: number;
//   debito_icms: number;
//   saldo_icms: number;
//   grupos: Grupo[];
// }

type TableIcmsEstruturaMercadologicaProps = {
  data: Secao[];
  title: string;
  loading?: boolean;
};

const formatCurrency = (value: number) =>
  value?.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }) ?? 'R$ 0,00';

const TableIcmsEstruturaMercadologica: React.FC<TableIcmsEstruturaMercadologicaProps> = ({ data, title, loading = false }) => {
  const [openSecoes, setOpenSecoes] = useState<Record<string, boolean>>({});
  const [openGrupos, setOpenGrupos] = useState<Record<string, boolean>>({});
  const theme = useTheme();

  const toggleSecao = (descricao: string) =>
    setOpenSecoes((prev) => ({ ...prev, [descricao]: !prev[descricao] }));

  const toggleGrupo = (descricao: string) =>
    setOpenGrupos((prev) => ({ ...prev, [descricao]: !prev[descricao] }));

  return (
    <Card sx={{ borderRadius: 3, p: 2, mt: 0 }}>
      <CardContent sx={{ p: 0 }}>
        {!loading && (
          <Box display="flex" alignItems="center" mb={2}>
            <Avatar
              sx={{
                bgcolor: theme.palette.error.light,
                color: theme.palette.error.main,
                width: 36,
                height: 36,
                mr: 1
              }}
            >
              <BalanceIcon />
            </Avatar>
            <Typography variant="subtitle1" sx={{ color: theme.palette.text.secondary }}>
              {title}
            </Typography>
          </Box>
        )}

        {loading ? (
          <HierarchicalTableSkeleton height={480} showTitle={false} />
        ) : (

        <TableContainer component={Paper} sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width="70%"><strong>Categoria</strong></TableCell>
                <TableCell align="right"><strong>Crédito ICMS</strong></TableCell>
                <TableCell align="right"><strong>Débito ICMS</strong></TableCell>
                <TableCell align="right"><strong>Saldo ICMS</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((secao, secaoIndex) => (
                <React.Fragment key={secao.descricao}>
                  <TableRow 
                    key={`${secao.descricao}`} 
                    sx={{
                      backgroundColor: secaoIndex % 2 === 0 ? '#ffffff' : '#f5f5f5'
                    }}
                  >
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <IconButton
                              size="small"
                              onClick={() => toggleSecao(secao.descricao)}
                              sx={{ mr: 1 }}
                            >
                              {openSecoes[secao.descricao] ? (
                                <KeyboardArrowUp />
                              ) : (
                                <KeyboardArrowDown />
                              )}
                            </IconButton>
                            <Typography fontWeight={600}>
                              {secao.descricao}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={400} color={theme.palette.success.main}>
                            {formatCurrency(secao.credito_icms)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={400} color={theme.palette.error.main}>
                            {formatCurrency(secao.debito_icms)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            fontWeight={400}
                            color={
                              (secao.saldo_icms ?? 0) > 0
                                ? theme.palette.error.main
                                : theme.palette.success.main
                            }
                          >
                            {formatCurrency(secao.saldo_icms)}
                          </Typography>
                        </TableCell>

                      </TableRow>

                  {openSecoes[secao.descricao] && secao.grupos.map((grupo, grupoIndex) => (
                    <React.Fragment key={grupo.descricao}>
                      <TableRow 
                        key={`${grupo.descricao}`} 
                        sx={{
                          backgroundColor: (secaoIndex * 10 + grupoIndex + 1) % 2 === 0 ? '#ffffff' : '#f5f5f5'
                        }}
                      >
                        <TableCell sx={{ pl: 6 }}>
                          <Box display="flex" alignItems="center">
                            <IconButton
                              size="small"
                              onClick={() => toggleGrupo(grupo.descricao)}
                              sx={{ mr: 1 }}
                            >
                              {openGrupos[grupo.descricao] ? (
                                <KeyboardArrowUp />
                              ) : (
                                <KeyboardArrowDown />
                              )}
                            </IconButton>
                            <Typography fontWeight={500}>
                              {grupo.descricao}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={400} color={theme.palette.success.main}>
                            {formatCurrency(grupo.credito_icms)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={400} color={theme.palette.error.main}>
                            {formatCurrency(grupo.debito_icms)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            fontWeight={400}
                            color={
                              (grupo.saldo_icms ?? 0) > 0
                                ? theme.palette.error.main
                                : theme.palette.success.main
                            }
                          >
                            {formatCurrency(grupo.saldo_icms)}
                          </Typography>
                        </TableCell>

                      </TableRow>

                      {openGrupos[grupo.descricao] &&
                        grupo.subgrupos.map((subgrupo, subgrupoIndex) => (
                          <TableRow 
                            key={`${subgrupo.descricao}`} 
                            sx={{
                              backgroundColor: (secaoIndex * 100 + grupoIndex * 10 + subgrupoIndex + 2) % 2 === 0 ? '#ffffff' : '#f5f5f5'
                            }}
                          >
                            <TableCell sx={{ pl: 10 }}>
                              {subgrupo.descricao}
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight={400} color={theme.palette.success.main}>
                                {formatCurrency(subgrupo.credito_icms)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight={400} color={theme.palette.error.main}>
                                {formatCurrency(subgrupo.debito_icms)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                fontWeight={400}
                                color={
                                  (subgrupo.saldo_icms ?? 0) > 0
                                    ? theme.palette.error.main
                                    : theme.palette.success.main
                                }
                              >
                                {formatCurrency(subgrupo.saldo_icms)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}

              {data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Nenhum dado encontrado.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default TableIcmsEstruturaMercadologica;
